<template>
  <div
    class="align-center bg- flex !h-screen !w-screen items-center justify-center bg-[linear-gradient(33deg,rgba(26,174,232,1)_0%,rgba(109,197,232,1)_50%,rgba(207,240,255,1)_100%)]"
  >
    <div
      class="h-fit w-11/12 rounded-xl bg-[rgba(255,255,255,0.5)] p-8 shadow backdrop-blur-md sm:w-10/12 md:w-[420px] md:p-12"
    >
      <div class="mb-4 flex items-center justify-center gap-4">
        <img src="@/assets/open-mdx.png" alt="logo openmdx" class="h-18 w-auto" />
      </div>

      <div class="text-sky-900">
        <label class="mb-2 block font-bold">
          {{ languageStore.t('auth.login.emailLabel') }}
        </label>
        <InputText
          id="email"
          type="text"
          :placeholder="languageStore.t('auth.login.placeholders.email')"
          @keydown.enter="login"
          v-model="form.email"
          :class="{ 'p-invalid': errors.email }"
          class="w-full"
        />
        <small v-if="errors.email" class="text-red-500">
          {{ errors.email }}
        </small>
        <label class="mt-4 block font-bold">
          {{ languageStore.t('auth.login.passwordLabel') }}
        </label>
        <div class="relative w-full">
          <InputText
            id="password"
            :type="showPassword"
            :placeholder="languageStore.t('auth.login.placeholders.password')"
            :class="{ 'p-invalid': errors.password }"
            class="w-full pr-10"
            v-model="form.password"
            @keydown.enter="login"
          />
          <Icons
            :icon="passwordIcon"
            :size="24"
            class="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
            @click="handlePassword"
          />
        </div>
        <small v-if="errors.password" class="text-red-500">{{ errors.password }}</small>
      </div>
      <div class="my-4">
        <RecoverPassword />
      </div>
      <Button
        :label="languageStore.t('auth.login.submitButtonAd')"
        class="mb-2 w-full"
        @click="loginAd"
        :loading="isLoading"
      />
      <Button
        :label="languageStore.t('auth.login.submitButton')"
        outlined
        class="w-full"
        @click="login"
        :loading="isLoading"
      />
      <Tag severity="danger" :value="errorMessage" v-if="errorMessage" class="mt-4 w-full" />
      <div class="mt-4 flex flex-col items-center justify-center gap-4">
        <img src="@/assets/logo-nepen.png" alt="logo nepen" class="h-8 w-auto" />
        <SelectLanguage :opacity="0.4" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import RecoverPassword from '@/components/login/RecoverPassword.vue'
import Icons from '@/components/subcomponents/Icons.vue'
import SelectLanguage from '@/components/subcomponents/SelectLanguage.vue'
import { resetUnauthenticatedState } from '@/composables/unauthenticatedToast'
import * as loginServices from '@/services/userService'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useLoginValidator } from '@/validators/loginValidator'
import { useToast } from 'primevue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const languageStore = useLanguageStore()
const toast = useToast()
const router = useRouter()

const { createForm, createEmptyErrors, normalizeCredential, validateAdForm, validateLocalForm } = useLoginValidator()

const form = reactive(createForm())
const errors = reactive(createEmptyErrors())
const isLoading = ref(false)
const showPassword = ref('password')
const passwordIcon = ref('eye-closed')
const errorMessage = ref('')

const handlePassword = () => {
  showPassword.value = showPassword.value === 'password' ? 'text' : 'password'
  passwordIcon.value = passwordIcon.value === 'eye-closed' ? 'eye-open' : 'eye-closed'
}

const login = () => {
  const validation = validateLocalForm(form)
  Object.assign(errors, validation.errors)
  if (!validation.isValid) {
    return
  }

  const normalizedEmail = normalizeCredential(form.email)

  isLoading.value = true
  errorMessage.value = ''

  loginServices
    .login(normalizedEmail, form.password)
    .then((res) => {
      resetUnauthenticatedState()
      const access = loginServices.auth(router, res.data.accessToken, res.data.user)
      if (!access) {
        errorMessage.value = 'Usuário não tem acesso a esta aplicação'
      }
    })
    .catch((error) => {
      errorMessage.value = error?.response?.data?.detail
      toast.add({
        summary: 'Erro',
        detail: errorMessage.value || 'Erro ao fazer login',
        severity: 'error',
        life: 3000,
      })
    })
    .finally(() => {
      isLoading.value = false
    })
}

const loginAd = () => {
  const validation = validateAdForm(form)
  Object.assign(errors, validation.errors)
  if (!validation.isValid) {
    return
  }

  const normalizedUsername = normalizeCredential(form.email)

  isLoading.value = true
  errorMessage.value = ''

  loginServices
    .loginAd(normalizedUsername, form.password, null)
    .then((res) => {
      const access = loginServices.auth(router, res.data.accessToken, res.data.user)
      if (!access) {
        errorMessage.value = 'Usuário não tem acesso a esta aplicação'
      }
    })
    .catch((error) => {
      errorMessage.value = error?.response?.data?.detail
      if (errorMessage.value === 'UserNotAssociated') {
        router.push({
          name: 'Associate',
          state: { ...form, email: normalizedUsername },
        })
        return
      }

      toast.add({
        summary: 'Erro',
        detail: errorMessage.value || 'Erro ao fazer login',
        severity: 'error',
        life: 3000,
      })
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>
